import { BuildingLibraryIcon, MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { intersection, keyBy, uniq } from "lodash";
import filter from "lodash/filter";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import {
  selectBondHolderFromActivePosition,
  selectCommodityOnBond,
  selectCommodityOnLease,
  selectCommodityThatHasEarnedInterest,
  selectLessorFromActivePosition,
} from "@/redux/positions/positionsSelectors";
import { useGetInterestAndTaxQuery } from "@/routes/_authenticated/yield/-api/yield";
import { useGetAccountPositionQuery } from "@/shared/api/account";
import { InterestEarnedChartContainer } from "@/shared/components/InterestEarnedChartContainer";
import MetalPageControls from "@/shared/components/MetalPageControls";
import type { AssetClass } from "@/shared/components/PageControls";
import { PortalTutorialButton } from "@/shared/components/PortalTutorialButton";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import useActiveAssetClass from "@/shared/hooks/useActiveAssetClass";

import YieldCard from "./yield/-components/YieldCard";

const YieldViewSkeleton = () => (
  <div className="flex flex-col gap-4 md:gap-9">
    <Skeleton className="h-14 rounded md:h-[104px] md:rounded-2xl" />
    <Skeleton className="h-[578px] rounded-2xl" />
    <Skeleton className="h-[470px] rounded-2xl" />
  </div>
);

const YieldViewEmptyState = () => {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <BuildingLibraryIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`You don't currently have any metal in yield. Join a lease now to start earning interest.`}</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Button color="primary" render={<Link to="/opportunities" />}>
          <MagnifyingGlassIcon className="mr-2 h-6 w-auto" />
          {t`Review Available Leases`}
        </Button>
        <PortalTutorialButton />
      </div>
    </div>
  );
};

function YieldView() {
  const eid = useSelector(selectMaybeActiveAccountEid);
  const positionQuery = useGetAccountPositionQuery(eid || skipToken);
  const interestQuery = useGetInterestAndTaxQuery(eid || skipToken);
  const commodityOnLease = useSelector(selectCommodityOnLease);
  const bondInfo = useSelector(selectBondHolderFromActivePosition);
  const commodityOnBond = useSelector(selectCommodityOnBond);
  const [activeAssetClass] = useActiveAssetClass();

  const commoditiesWithInterest = useSelector(selectCommodityThatHasEarnedInterest);
  const commoditiesParticipatingInYield = useMemo(
    () => uniq([...commodityOnLease, ...commodityOnBond]),
    [commodityOnLease, commodityOnBond],
  );
  const supportedAssets = useMemo(() => {
    const assets: AssetClass[] = uniq([...commoditiesParticipatingInYield, ...commoditiesWithInterest]);

    if (assets.length > 1) {
      assets.unshift("total");
    }

    return assets;
  }, [commoditiesParticipatingInYield, commoditiesWithInterest]);

  const lessor = useSelector(selectLessorFromActivePosition);

  const activeDataByCommodity = useMemo(
    () =>
      keyBy(
        filter([...lessor, ...bondInfo], (deal) => deal.status === "active"),
        "commodity",
      ),
    [bondInfo, lessor],
  );

  const leaseCommoditiesToShow =
    activeAssetClass === "total" ? commodityOnLease : intersection(commodityOnLease, [activeAssetClass]);
  const bondCommoditiesToShow =
    activeAssetClass === "total" ? commodityOnBond : intersection(commodityOnBond, [activeAssetClass]);

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<YieldViewSkeleton />}
      shouldRetry
      queries={[positionQuery, interestQuery]}
      translatedErrorMessage={t`There was an issue loading your investments. Please try again later or contact your RM if the problem persists.`}
    >
      {Object.keys(activeDataByCommodity).length === 0 ? (
        <YieldViewEmptyState />
      ) : (
        <div className="flex flex-col gap-4 md:gap-9">
          <MetalPageControls supportedAssets={supportedAssets} />
          <InterestEarnedChartContainer defaultOpen />
          <div className="flex flex-col gap-4">
            {uniq([...leaseCommoditiesToShow, ...bondCommoditiesToShow]).map((commmodity) => {
              const leaseData = filter(lessor, (lease) => lease.commodity === commmodity && lease.status === "active");
              const bondData = filter(bondInfo, (bond) => bond.commodity === commmodity && bond.status === "active");

              return (
                <YieldCard
                  key={`yield_card_${commmodity}`}
                  commodity={commmodity}
                  leaseData={leaseData}
                  bondData={bondData}
                />
              );
            })}
          </div>
        </div>
      )}
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/yield")({
  component: YieldView,
  context: () => ({ permissions: "investment" }),
});
